import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {

    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormTouched } from '@/hooks/use-form-touched';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

function organisationsList() {
    return api.queryOptions('get', '/api/v2/organizations/list', {
        params: {
            query: {
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    Name: true,
                    Description: true,
                    ElectricalOrganizationKind: true,
                    LogoOrganizationFileId: true,
                    ChamberOfCommerceNumber: true,
                    LegalPersonKind: true,
                },
                Sort: ['Name'],
            },
        },
    });
}

export const Route = createFileRoute(
    '/(auth)/admin/(organisations)/organisations/',
)({
    component: Page,
});

function Page() {
    return (
        <Container>
            <Container.Content>
                <Form />
            </Container.Content>
        </Container>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function getColumns(t: (key: string) => string): ColumnDef<
    components['schemas']['ListOrganizationQueryResultOrganization']
>[] {
    return [
        {
            accessorKey: 'Name',
            header: t('organisations.table.columns.name'),
        },
        {
            accessorKey: 'Description',
            header: t('organisations.table.columns.description'),
        },
        {
            accessorKey: 'openOrganizationProjects',
            header: '',
            cell: ({ row }) => {
                const project = row.original;

                return (
                    <div className="flex flex-row justify-center">
                        <Link
                            to="/projects"
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            search={{
                                organizationId: project.OrganizationId!,
                            }}
                        >
                            {t('organisations.actions.openProjects')}
                        </Link>
                    </div>
                );
            },
        },
        {
            accessorKey: 'openOrganization',
            header: '',
            cell: ({ row }) => {
                const project = row.original;

                return (
                    <div className="flex flex-row justify-center">
                        <Link
                            to="/admin/organisation"
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            search={{
                                organisationId: project.OrganizationId!,
                            }}
                        >
                            {t('organisations.actions.openOrganization')}
                        </Link>
                    </div>
                );
            },
        },
    ];
}

type DataTableProps<TData, TValue> = {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
};

export function DataTable<TData, TValue>({
    columns,
    data,
}: DataTableProps<TData, TValue>) {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <ScrollArea className="max-h-[800px] overflow-auto">
            <Table className="border">
                <TableHeader>
                    {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {
                        table.getRowModel().rows?.length
                            ? (
                                    table.getRowModel().rows.map(row => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && 'selected'}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <TableCell
                                                    key={cell.id}
                                                    className="px-4 py-2"
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext(),
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )
                            : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center"
                                        >
                                            Geen resultaten gevonden.
                                            {/** TODO: Translation */}
                                        </TableCell>
                                    </TableRow>
                                )
                    }
                </TableBody>
            </Table>
        </ScrollArea>
    );
}

type FormValues = {
    name: string;
    description: string;
    sourceKind: 'NLKVK';
    nLKvK: {
        KVKNumber: string;
    };
    slogan: string;
    email: string;
    telephoneNumber: string;
    website: string;
    iBAN: string;
    bIC: string;
    vatNumber: string;
};

function Form() {
    const { t } = useTranslation('admin');
    const projectsQuery = useQuery(organisationsList());
    const columns = getColumns(t);

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
            }}
            className="flex flex-col gap-4 p-6"
        >
            <DataTable
                columns={columns}
                data={projectsQuery?.data?.Organizations.filter(x => x.ElectricalOrganizationKinds?.includes('Inspection')) ?? []}
            />
            {' '}
            {/** TODO: Add pagination */}
            <CreateOrganisationModal />
        </form>
    );
}

function CreateOrganisationModal() {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation('admin');
    const { t: c } = useTranslation('common');

    const createOrganisationMutation = api.useMutation(
        'post',
        '/api/v2/organizations/create',
        {
            onSuccess: () => {
                toast.success(t('organisations.messages.organisationCreated'));
                queryClient.invalidateQueries(organisationsList());
            },
        },
    );

    const defaultValues: FormValues = {
        name: '',
        description: '',
        sourceKind: 'NLKVK',
        nLKvK: {
            KVKNumber: '',
        },
        slogan: '',
        email: '',
        telephoneNumber: '',
        website: '',
        iBAN: '',
        bIC: '',
        vatNumber: '',
    };

    const { Field, handleSubmit, reset, store } = useForm<FormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            const body: components['schemas']['CreateOrganizationCommand'] = {
                Name: value.name,
                Description: value.description,
                SourceKind: value.sourceKind,
                NLKvK: {
                    KVKNumber: value.nLKvK.KVKNumber,
                },
                Slogan: value.slogan,
                ChamberOfCommerceNumber: value.nLKvK.KVKNumber,
                TelephoneNumber: value.telephoneNumber,
                IBAN: value.iBAN,
                BIC: value.bIC,
                VatNumber: value.vatNumber,
            };

            if (value.email) {
                body.Email = value.email;
            }

            if (value.website) {
                body.Website = value.website;
            }

            await createOrganisationMutation.mutateAsync({
                body,
            });

            setIsOpen(false);
            reset();
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(t('organisations.messages.confirmClose'))) {
                setIsOpen(false);
                reset();
                createOrganisationMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
            createOrganisationMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                <Button className="w-full">{c('actions.selection.add')}</Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('organisations.actions.createOrganization')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form
                        onSubmit={onSubmit}
                        className="flex flex-col gap-4 py-6"
                    >
                        <Field
                            name="name"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.name,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.name.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.name.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="description"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.description.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.description.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="nLKvK.KVKNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.createOrganizationCommandNLKvK.kVKNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.chamberOfCommerceNumber.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.chamberOfCommerceNumber.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="slogan"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.slogan,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.slogan.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.slogan.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="email"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.email,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.email.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.email.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="telephoneNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.telephoneNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.phone.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.phone.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="website"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.website,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.website.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.website.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="iBAN"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.iBAN,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.iban.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.iban.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="bIC"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.bIC,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.bic.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.bic.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="vatNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.vatNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisations.form.vatNumber.label')}>
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisations.form.vatNumber.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </form>
                </PopoutBody>
                <PopoutFooter className="flex flex-col gap-4">
                    <div className="w-full flex flex-row gap-2">
                        <Button className="w-full" variant="outline" onClick={handleClose} disabled={createOrganisationMutation.isPending}>{c('actions.general.cancel')}</Button>
                        <Button className="w-full" onClick={handleSubmit} disabled={createOrganisationMutation.isPending}>{c('actions.general.save')}</Button>
                    </div>
                    <em>
                        {createOrganisationMutation.isPending
                            ? t('organisations.messages.saving')
                            : createOrganisationMutation.isError
                                ? t('organisations.messages.error', { message: createOrganisationMutation.error.Message })
                                : null}
                    </em>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
